import { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Trophy, Loader2, XCircle, ShieldCheck, BookOpen, BarChart3, Telescope,
  Sparkles, FileText, Tally5, Award, CircleAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

/**
 * ReadinessDashboard — Sprint 6 finale.
 *
 * Single-click "is this chapter publication-ready?" audit. Runs:
 *   • Citation Verifier (free, already computed)
 *   • Citation density heuristic (free)
 *   • Word count vs target (free)
 *   • EQUATOR compliance against the project guideline (LLM, in-parallel)
 *   • Statistical Reporting Checker (Wave D — LLM, in-parallel)
 *   • Research Gap Detector (Wave D — LLM, in-parallel)
 *   • Novelty Checker (Wave D — LLM, in-parallel)
 *
 * Aggregates a weighted 0-100 readiness score + a flat, deduped, priority-
 * sorted list of ≤ 12 action items the author can paste into a defence-prep
 * TODO list.
 */
const CATEGORY_ICONS = {
  citation_verifier: ShieldCheck,
  citation_density: BookOpen,
  equator_compliance: ShieldCheck,
  statistical_reporting: BarChart3,
  gap_coverage: Telescope,
  novelty: Sparkles,
  word_count: Tally5,
};

const CATEGORY_LABELS = {
  citation_verifier: 'Citation verifier',
  citation_density: 'Citation density',
  equator_compliance: 'EQUATOR compliance',
  statistical_reporting: 'Statistical reporting',
  gap_coverage: 'Library coverage',
  novelty: 'Novelty',
  word_count: 'Length vs target',
};

function scoreColor(s) {
  if (s >= 80) return 'text-emerald-600 dark:text-emerald-400';
  if (s >= 60) return 'text-amber-600 dark:text-amber-400';
  if (s >= 40) return 'text-orange-600 dark:text-orange-400';
  return 'text-red-600 dark:text-red-400';
}
function ringStroke(s) {
  if (s >= 80) return '#10b981';
  if (s >= 60) return '#f59e0b';
  if (s >= 40) return '#f97316';
  return '#ef4444';
}

function ScoreRing({ score }) {
  const radius = 64;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (Math.max(0, Math.min(100, score)) / 100) * circ;
  return (
    <svg width="160" height="160" viewBox="0 0 160 160" data-testid="readiness-score-ring">
      <circle cx="80" cy="80" r={radius} fill="none" stroke="#e2e8f0" strokeWidth="12" className="dark:stroke-slate-700" />
      <circle cx="80" cy="80" r={radius} fill="none" stroke={ringStroke(score)} strokeWidth="12"
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        transform="rotate(-90 80 80)" style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
      <text x="80" y="84" textAnchor="middle" fontSize="36" fontWeight="700"
        className="fill-slate-800 dark:fill-slate-100">
        {score}
      </text>
      <text x="80" y="108" textAnchor="middle" fontSize="11"
        className="fill-slate-500 dark:fill-slate-400 uppercase tracking-wider">
        readiness
      </text>
    </svg>
  );
}

function priorityBadge(p) {
  const styles = {
    high: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300',
    medium: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
    low: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
  };
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full uppercase ${styles[p] || styles.medium}`}>
      {p}
    </span>
  );
}

export default function ReadinessDashboardDialog({
  open, onOpenChange,
  userId, projectId, chapter, language = 'en',
}) {
  const [job, setJob] = useState(null);
  const pollerRef = useRef(null);

  useEffect(() => {
    if (!open && pollerRef.current) {
      clearInterval(pollerRef.current);
      pollerRef.current = null;
    }
    return () => {
      if (pollerRef.current) clearInterval(pollerRef.current);
    };
  }, [open]);

  const start = useCallback(async () => {
    if (!chapter?.id || !projectId) return;
    setJob({ status: 'queued', progress: 'Starting…', result: null });
    try {
      const { job_id } = await thesisApi.startReadinessAudit(userId, {
        project_id: projectId,
        chapter_id: chapter.id,
        n_chunks: 20,
        retrieval_mode: 'hybrid',
        target_words: chapter.min_words || 3000,
        language,
      });
      const started = Date.now();
      if (pollerRef.current) clearInterval(pollerRef.current);
      pollerRef.current = setInterval(async () => {
        if (Date.now() - started > 6 * 60 * 1000) {
          clearInterval(pollerRef.current); pollerRef.current = null;
          setJob((j) => ({ ...j, status: 'failed', error: 'Timed out after 6 minutes' }));
          return;
        }
        try {
          const j = await thesisApi.pollReadinessJob(userId, job_id);
          setJob(j);
          if (j.status === 'ready' || j.status === 'failed') {
            clearInterval(pollerRef.current); pollerRef.current = null;
          }
        } catch { /* network blip */ }
      }, 3000);
    } catch (e) {
      toast.error(`Could not start audit: ${e?.response?.data?.detail || e.message}`);
      setJob({ status: 'failed', error: e.message });
    }
  }, [chapter, projectId, userId, language]);

  const status = job?.status;
  const result = job?.result;
  const showStart = !status || status === 'failed';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="readiness-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Trophy className="h-5 w-5 text-amber-500" />
            Pre-publish Readiness Score
          </DialogTitle>
          <DialogDescription>
            <strong>{chapter?.title || '(no chapter selected)'}</strong> — combined audit of citations, EQUATOR compliance, statistical reporting, library coverage, and novelty.
          </DialogDescription>
        </DialogHeader>

        {!status && (
          <div className="space-y-3">
            <div className="rounded-lg border border-amber-200 bg-amber-50/60 dark:bg-amber-950/20 dark:border-amber-900 px-4 py-3 text-sm text-amber-900 dark:text-amber-200 leading-relaxed">
              This audit runs four LLM analyzers in parallel and takes ~ 30-60 seconds. The result is persisted on the chapter so you can revisit it without re-running.
            </div>
          </div>
        )}

        {(status === 'queued' || status === 'running') && (
          <div className="py-12 text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-amber-500" />
            <p className="text-sm text-slate-700 dark:text-slate-200 mt-4 font-medium">{job.progress}</p>
            <p className="text-xs text-slate-500 mt-1">Running 4 analyzers in parallel — ~ 30-60 s total.</p>
          </div>
        )}

        {status === 'failed' && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-900 px-4 py-3 flex items-start gap-3">
            <XCircle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
            <div className="text-sm text-red-900 dark:text-red-200">
              <p className="font-medium">Audit failed</p>
              <p className="text-xs opacity-80 mt-0.5">{job.error || 'Unknown error'}</p>
            </div>
          </div>
        )}

        {status === 'ready' && result && (
          <ResultBody result={result} />
        )}

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 dark:border-slate-800 mt-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="readiness-close">Close</Button>
          {showStart && (
            <Button onClick={start} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="readiness-start">
              <Trophy className="w-4 h-4 mr-2" />
              {status === 'failed' ? 'Retry audit' : 'Run readiness audit'}
            </Button>
          )}
          {status === 'ready' && (
            <Button onClick={start} variant="outline" data-testid="readiness-rerun">
              Re-run
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ResultBody({ result }) {
  const score = result.score || 0;
  const breakdown = result.breakdown || {};
  const actions = result.action_items || [];
  const code = result.code_evidence || null;
  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-gradient-to-br from-amber-50/60 to-violet-50/40 dark:from-amber-950/20 dark:to-violet-950/20 p-5 flex items-center gap-6">
        <ScoreRing score={score} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Award className={`h-5 w-5 ${scoreColor(score)}`} />
            <h3 className={`text-lg font-bold ${scoreColor(score)}`}>
              {score >= 80 ? 'Ready for submission' : score >= 60 ? 'Almost there' : score >= 40 ? 'Needs work' : 'Major revisions needed'}
            </h3>
          </div>
          <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            {actions.length === 0
              ? 'No outstanding action items — this chapter passes the pre-publish audit.'
              : `${actions.filter((a) => a.priority === 'high').length} high-priority and ${actions.filter((a) => a.priority === 'medium').length} medium-priority items detected. Review the list below.`}
          </p>
        </div>
      </div>

      {code?.has_code_evidence && (
        <div className="rounded-lg border border-sky-200 dark:border-sky-900 bg-sky-50 dark:bg-sky-950/30 p-3"
          data-testid="readiness-code-evidence">
          <p className="text-sm font-semibold text-sky-900 dark:text-sky-200">Code-derived evidence</p>
          <p className="text-xs text-sky-800 dark:text-sky-300 mt-1">
            {code.marker_count} code anchor(s) across {code.code_sentences} sentence(s) count as internal
            (code-verified) evidence. {code.ref_needed?.length || 0} statement(s) still need a literature
            reference and {code.validation_required?.length || 0} need a validation study — both are listed
            as action items below.
          </p>
        </div>
      )}

      <div>
        <h4 className="text-sm font-semibold mb-2 text-slate-700 dark:text-slate-200 uppercase tracking-wider">Score breakdown</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {Object.entries(breakdown).map(([key, b]) => {
            const Icon = CATEGORY_ICONS[key] || ShieldCheck;
            return (
              <div key={key} className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                data-testid={`readiness-component-${key}`}>
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-slate-500 shrink-0" />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-200 flex-1">
                    {CATEGORY_LABELS[key] || key}
                  </span>
                  <span className={`text-lg font-bold ${scoreColor(b.score || 0)}`}>
                    {b.score}<span className="text-[10px] text-slate-400 ml-0.5">/100</span>
                  </span>
                  <span className="text-[10px] text-slate-400 ml-1">×{(b.weight * 100).toFixed(0)}%</span>
                </div>
                {b.note && (
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-snug mt-1">{b.note}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <h4 className="text-sm font-semibold mb-2 text-slate-700 dark:text-slate-200 uppercase tracking-wider flex items-center gap-2">
          <FileText className="h-4 w-4" /> Action items ({actions.length})
        </h4>
        {actions.length === 0 ? (
          <p className="text-sm text-emerald-700 dark:text-emerald-300 italic">All checks passed — nothing to act on.</p>
        ) : (
          <ol className="space-y-2">
            {actions.map((a, i) => (
              <li key={i} className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                data-testid={`readiness-action-${i}`}>
                <div className="flex items-start gap-2">
                  <CircleAlert className={`h-4 w-4 mt-0.5 shrink-0 ${
                    a.priority === 'high' ? 'text-red-500' : a.priority === 'medium' ? 'text-amber-500' : 'text-slate-400'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      {priorityBadge(a.priority)}
                      <span className="text-[10px] uppercase tracking-wider text-slate-500">{a.category}</span>
                    </div>
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{a.label}</p>
                    {a.suggestion && (
                      <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 leading-relaxed">{a.suggestion}</p>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
